/**
 * Terminology Mapping Layer (v2.2)
 * 
 * Single source of truth for UI labels that maps Terminology v2.2 vocabulary
 * to backend reality. This allows the UI to always show correct v2.2 terms
 * while code internally uses whatever the backend returns.
 * 
 * Reference: DOCS_v2/02 — Terminology and Glossary (v2.2).md
 */

// =============================================================================
// Core Terminology Definitions
// =============================================================================

export const TERMINOLOGY = {
  // Primary Object
  ITEM: {
    label: 'Item',
    labelPlural: 'Items',
    dbTable: 'items',
    legacyTerms: ['candidate', 'candidates'],
    description: 'The primary first-class object in the system.',
  },

  // Pool
  ITEM_POOL: {
    label: 'Item Pool',
    labelPlural: 'Item Pools',
    dbTable: 'item_pools',
    legacyTerms: ['focus_list', 'focus_lists', 'candidate_pool', 'candidate_pools'],
    description: 'The comparison set selected during Arrange.',
  },

  // Roles
  BALL_HOLDER: {
    label: 'Ball Holder',
    labelPlural: 'Ball Holders',
    dbRole: 'STAGE_OWNER',
    legacyTerms: ['stage_owner', 'Stage Owner', 'STAGE_OWNER'],
    description: 'The role currently responsible for advancing the Ball at a given Tier.',
  },

  // Governance
  PUBLISHED_PRIORITY_LIST: {
    label: 'Published Priority List',
    labelPlural: 'Published Priority Lists',
    dbTable: 'priority_sets',
    legacyTerms: [],
    description: 'The authoritative prioritization outcome after Ratified → Published.',
  },

  // Containers
  CYCLE: {
    label: 'Cycle',
    labelPlural: 'Cycles',
    dbTable: 'degap_cycles',
    legacyTerms: [],
    description: 'The highest-level container of intent for prioritization.',
  },

  LOOP: {
    label: 'Loop',
    labelPlural: 'Loops',
    dbTable: 'catchball_loops',
    legacyTerms: [],
    description: 'One complete Request → Proposal pass within a Cycle.',
  },

  PROPOSAL: {
    label: 'Proposal',
    labelPlural: 'Proposals',
    dbTable: 'catchball_proposals',
    legacyTerms: [],
    description: 'The upward form of the Ball containing Track results.',
  },

  REQUEST: {
    label: 'Request',
    labelPlural: 'Requests',
    dbTable: null,
    legacyTerms: [],
    description: 'The downward form of the Ball containing intent and constraints.',
  },

  // Sessions
  SESSION: {
    label: 'Session',
    labelPlural: 'Sessions',
    dbTable: 'prioritization_sessions',
    legacyTerms: [],
    description: 'The canonical unit of work where prioritization occurs.',
  },

  SESSION_RESULT: {
    label: 'Session Result',
    labelPlural: 'Session Results',
    dbTable: 'session_reports',
    legacyTerms: [],
    description: 'Immutable output of a single session.',
  },

  // Geometry
  TRACK: {
    label: 'Track',
    labelPlural: 'Tracks',
    dbTable: 'tracks',
    legacyTerms: ['Comparison Frame', 'comparison frame', 'Track Instance'],
    description: 'The persistent work container between a Cycle and its Events: owns Sessions and Results and binds to a Cycle; its Governance Posture is derived from Proposal outcomes (authority lives in the ball lane, not the Track). Uniformity rules live in its Session Baseline; behavioral defaults come from the Session Policy cascade. (Canon 28)',
  },

  TRACK_CELL: {
    label: 'Comparison Cell',
    labelPlural: 'Comparison Cells',
    dbTable: null,
    legacyTerms: ['Grid Cell', 'grid_cell', 'Track Cell', 'track_cell', 'Frame Cell'],
    description: 'The intersection of Item Category and Altitude that anchors a Track — one cell of the Comparison Grid.',
  },

  SESSION_BASELINE: {
    label: 'Session Baseline',
    labelPlural: 'Session Baselines',
    dbTable: null, // target: session_baselines (canon 28 Phase A); today lives as columns on tracks
    legacyTerms: [],
    description: 'The content lock that enforces uniformity across all Sessions within a Track: items, criteria, and value ranges that must be identical so results are aggregable. A constraint, not a default — no Session may override it. (Canon 28)',
  },

  // Routing (canon 29)
  PATH: {
    label: 'Path',
    labelPlural: 'Paths',
    dbTable: null, // durable route; today stored as track designer metadata + CatchballVirtualConfig
    legacyTerms: ['Lane', 'lane'],
    description: 'The durable, reusable route a request travels — anchor to endpoint, org-chart or custom. A Path has Loops; a Loop runs over exactly one Path. (Canon 29)',
  },

  PATH_INSTANCE: {
    label: 'Path Instance',
    labelPlural: 'Path Instances',
    dbTable: null,
    legacyTerms: ['Lane Instance'],
    description: 'The traversal of a Path within one Loop — the walk, not the road — including any Splits taken. (Canon 29)',
  },

  ROLLOUT: {
    label: 'Rollout',
    labelPlural: 'Rollouts',
    dbTable: null,
    legacyTerms: ['Ball Tree'],
    description: "The downward motion of a Loop: a Request fans out across branches, each landing minting a child Ball. (Canon 29)",
  },

  ROLLUP: {
    label: 'Rollup',
    labelPlural: 'Rollups',
    dbTable: null,
    legacyTerms: [],
    description: 'The upward motion of a Loop: sibling Proposals merge at each Split Node until the root Ball holds one answer. Qualify as "Ball rollup" where composition roll-up could co-occur. (Canon 29)',
  },

  SESSION_POLICY: {
    label: 'Session Policy',
    labelPlural: 'Session Policies',
    dbTable: null, // target: session_policies (canon 28 Phase A); today lives as default_* columns on workspaces/degap_cycles/tracks
    legacyTerms: [],
    description: 'The behavioral defaults bundle that cascades Workflow → Cycle → Track → Session (timing mode, entry mode, facilitator). A starting point, not a lock — each level may override without invalidating results. (Canon 28)',
  },

  ITEM_CATEGORY: {
    label: 'Item Category',
    labelPlural: 'Item Categories',
    dbColumn: 'item_category',
    legacyTerms: ['candidate_type', 'candidateType'],
    description: 'Workspace-defined classification for comparison.',
  },

  ALTITUDE: {
    label: 'Altitude',
    labelPlural: 'Altitudes',
    dbColumn: 'altitude_level',
    legacyTerms: [],
    description: 'A 1–5 measure of decision significance.',
  },

  // Governance Events
  REVISION_REQUEST: {
    label: 'Revision Request',
    labelPlural: 'Revision Requests',
    dbTable: null,
    legacyTerms: [],
    description: 'Initiates another Loop within the same Track.',
  },

  FINAL_REJECTION: {
    label: 'Final Rejection',
    labelPlural: 'Final Rejections',
    dbTable: null,
    legacyTerms: [],
    description: 'Explicitly terminates a Track without further revision.',
  },

  // Evaluation
  CRITERIA_SET: {
    label: 'Criteria Set',
    labelPlural: 'Criteria Sets',
    dbTable: 'cycle_comparison_sets',
    legacyTerms: [],
    description: 'Named collection of criteria for a prioritization context.',
  },

  // Decision Classes (Session Types) - S1 through S6
  DECISION_CLASS: {
    label: 'Decision Class',
    labelPlural: 'Decision Classes',
    dbColumn: 'decision_class',
    legacyTerms: ['session_type', 'sessionType'],
    description: 'The semantic definition of what decision is being made within a Session.',
  },

  // Catchball Actions
  TOSS: {
    label: 'Toss',
    labelPlural: 'Tosses',
    dbTable: null,
    legacyTerms: [],
    description: 'The event of transferring the Ball from one tier to another.',
  },

  // Cluster Relationships
  CLUSTER: {
    label: 'Cluster',
    labelPlural: 'Clusters',
    dbTable: 'item_clusters',
    legacyTerms: [],
    description: 'A relationship container among Items for similarity, hierarchy, or aggregation.',
  },

  // Roles
  SHAPER: {
    label: 'Item Manager',
    labelPlural: 'Item Managers',
    dbRole: 'STEWARD',
    legacyTerms: ['steward', 'STEWARD'],
    description: 'Accountable for turning an intake item into a usable Item with category and altitude.',
  },

  CLUSTERER: {
    label: 'Clusterer',
    labelPlural: 'Clusterers',
    dbRole: null,
    legacyTerms: [],
    description: 'Owns cluster relationships: duplicate/variant membership and cross-altitude links.',
  },

  // Deprecated terms (for reference and mapping)
  // These should never be displayed in UI
} as const;

export type TerminologyKey = keyof typeof TERMINOLOGY;

// =============================================================================
// Legacy Term Mappings
// =============================================================================

const LEGACY_TO_CANONICAL: Record<string, TerminologyKey> = {};

// Build reverse mapping from legacy terms to canonical keys
for (const [key, value] of Object.entries(TERMINOLOGY)) {
  for (const legacyTerm of value.legacyTerms) {
    LEGACY_TO_CANONICAL[legacyTerm.toLowerCase()] = key as TerminologyKey;
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the canonical UI label for a terminology key
 */
export function getLabel(key: TerminologyKey): string {
  return TERMINOLOGY[key].label;
}

/**
 * Get the plural UI label for a terminology key
 */
export function getLabelPlural(key: TerminologyKey): string {
  return TERMINOLOGY[key].labelPlural;
}

/**
 * Get the description for a terminology key
 */
export function getDescription(key: TerminologyKey): string {
  return TERMINOLOGY[key].description;
}

/**
 * Map a backend term to the canonical UI label.
 * Returns the original term if no mapping exists.
 */
export function mapBackendToUI(backendTerm: string): string {
  const normalized = backendTerm.toLowerCase().replace(/_/g, ' ');
  
  // Direct mappings
  const mappings: Record<string, string> = {
    'candidate': 'Item',
    'candidates': 'Items',
    'focus list': 'Item Pool',
    'focus lists': 'Item Pools',
    'focus_list': 'Item Pool',
    'focus_lists': 'Item Pools',
    'candidate pool': 'Item Pool',
    'candidate pools': 'Item Pools',
    'candidate_pool': 'Item Pool',
    'stage owner': 'Ball Holder',
    'stage_owner': 'Ball Holder',
    'proto': 'Item', // proto is deprecated; everything is Item
  };

  return mappings[normalized] || backendTerm;
}

/**
 * Map a backend role to the canonical UI label
 */
export function mapRoleToUI(role: string): string {
  const roleMap: Record<string, string> = {
    'STAGE_OWNER': 'Ball Holder',
    'CYCLE_OWNER': 'Cycle Owner',
    'DECISION_AUTHORITY': 'Decision Authority',
    'FACILITATOR': 'Facilitator',
    'EXPERT': 'Expert',
    'CONTRIBUTOR': 'Contributor',
    'OBSERVER': 'Observer',
    'STEWARD': 'Item Manager',
    'SHAPER': 'Item Manager',
    'CLUSTERER': 'Clusterer',
    'LIST_OWNER': 'Pool Owner',
    'CRITERIA_AUTHORITY': 'Criteria Authority',
  };

  return roleMap[role] || role;
}

// =============================================================================
// Decision Class (Session Type) Labels - S1 through S6
// =============================================================================

export const DECISION_CLASS_LABELS: Record<string, { code: string; label: string; description: string }> = {
  'S1': {
    code: 'S1',
    label: 'Participation Prioritization',
    description: 'Determine who participates and their roles.',
  },
  'S2': {
    code: 'S2',
    label: 'Item Pool Selection',
    description: 'Select which Items enter the prioritization pool.',
  },
  'S3': {
    code: 'S3',
    label: 'Attribute Selection',
    description: 'Select which attributes/criteria will be used for evaluation.',
  },
  'S4': {
    code: 'S4',
    label: 'Values Choice Selection',
    description: 'Define value scales and choice options for attributes.',
  },
  'S5': {
    code: 'S5',
    label: 'Value Selection',
    description: 'Assign values to Items for selected attributes.',
  },
  'S6': {
    code: 'S6',
    label: 'Item Prioritization',
    description: 'Rank Items using prioritization tools.',
  },
  // Legacy mappings
  'PARTICIPATION_PRIORITIZATION': { code: 'S1', label: 'Participation Prioritization', description: 'Determine who participates and their roles.' },
  'ITEM_POOL_SELECTION': { code: 'S2', label: 'Item Pool Selection', description: 'Select which Items enter the prioritization pool.' },
  'CANDIDATE_POOL_SELECTION': { code: 'S2', label: 'Item Pool Selection', description: 'Select which Items enter the prioritization pool.' },
  'ATTRIBUTE_SELECTION': { code: 'S3', label: 'Attribute Selection', description: 'Select which attributes/criteria will be used for evaluation.' },
  'VALUES_CHOICE_SELECTION': { code: 'S4', label: 'Values Choice Selection', description: 'Define value scales and choice options for attributes.' },
  'VALUE_SELECTION': { code: 'S5', label: 'Value Selection', description: 'Assign values to Items for selected attributes.' },
  'ITEM_PRIORITIZATION': { code: 'S6', label: 'Item Prioritization', description: 'Rank Items using prioritization tools.' },
  'CANDIDATE_PRIORITIZATION': { code: 'S6', label: 'Item Prioritization', description: 'Rank Items using prioritization tools.' },
};

/**
 * Get the canonical Decision Class label for a session type
 */
export function getDecisionClassLabel(sessionType: string): { code: string; label: string; description: string } {
  const normalized = sessionType.toUpperCase().replace(/-/g, '_');
  return DECISION_CLASS_LABELS[normalized] || DECISION_CLASS_LABELS[sessionType] || {
    code: sessionType,
    label: sessionType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    description: '',
  };
}

// =============================================================================
// Cluster Relationship Type Labels
// =============================================================================

export const CLUSTER_RELATIONSHIP_LABELS: Record<string, { label: string; category: string; description: string }> = {
  'duplicate': {
    label: 'Duplicate',
    category: 'Similarity',
    description: 'Same request, different wording. Children are aliases; parent is canonical.',
  },
  'variant': {
    label: 'Variant',
    category: 'Similarity',
    description: 'Same core need, different scope/approach. Parent represents shared need.',
  },
  'composition': {
    label: 'Composition',
    category: 'Aggregation',
    description: 'Children rolled up into parent. Bundle that travels together.',
  },
  'decomposition': {
    label: 'Decomposition',
    category: 'Hierarchy',
    description: 'Parent broken into children. Work breakdown structure.',
  },
  // Legacy mappings
  'DUPLICATE': { label: 'Duplicate', category: 'Similarity', description: 'Same request, different wording.' },
  'VARIANT': { label: 'Variant', category: 'Similarity', description: 'Same core need, different approach.' },
  'COMPOSITION': { label: 'Composition', category: 'Aggregation', description: 'Children rolled up into parent.' },
  'DECOMPOSITION': { label: 'Decomposition', category: 'Hierarchy', description: 'Parent broken into children.' },
  'near_duplicate': { label: 'Near Duplicate', category: 'Similarity', description: 'Very similar requests.' },
  'related': { label: 'Related', category: 'Similarity', description: 'Connected but distinct (deprecated).' },
  'parent_child': { label: 'Parent-Child', category: 'Hierarchy', description: 'Hierarchical relationship.' },
};

/**
 * Get the canonical cluster relationship label
 */
export function getClusterRelationshipLabel(relationshipType: string): { label: string; category: string; description: string } {
  const normalized = relationshipType.toLowerCase();
  return CLUSTER_RELATIONSHIP_LABELS[normalized] || CLUSTER_RELATIONSHIP_LABELS[relationshipType] || {
    label: relationshipType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    category: 'Unknown',
    description: '',
  };
}

/**
 * Check if a term is deprecated
 */
export function isDeprecatedTerm(term: string): boolean {
  const deprecated = [
    'candidate', 'candidates', 'proto', 'focus list', 'focus_list',
    'candidate pool', 'candidate_pool', 'stage owner', 'stage_owner',
    'candidate_lifecycle_state',
  ];
  return deprecated.includes(term.toLowerCase());
}

/**
 * Get the canonical key for a legacy term
 */
export function getCanonicalKey(legacyTerm: string): TerminologyKey | undefined {
  return LEGACY_TO_CANONICAL[legacyTerm.toLowerCase()];
}

// =============================================================================
// UI Label Constants (for direct import in components)
// =============================================================================

export const UI_LABELS = {
  // Primary objects
  item: 'Item',
  items: 'Items',
  itemPool: 'Item Pool',
  itemPools: 'Item Pools',
  
  // Roles
  ballHolder: 'Ball Holder',
  ballHolders: 'Ball Holders',
  shaper: 'Item Manager',
  shapers: 'Item Managers',
  clusterer: 'Clusterer',
  clusterers: 'Clusterers',
  
  // Containers
  cycle: 'Cycle',
  cycles: 'Cycles',
  loop: 'Loop',
  loops: 'Loops',
  track: 'Track',
  tracks: 'Tracks',
  session: 'Session',
  sessions: 'Sessions',
  cluster: 'Cluster',
  clusters: 'Clusters',
  
  // Governance
  proposal: 'Proposal',
  proposals: 'Proposals',
  request: 'Request',
  requests: 'Requests',
  publishedPriorityList: 'Published Priority List',
  publishedPriorityLists: 'Published Priority Lists',
  
  // Actions
  revisionRequest: 'Revision Request',
  finalRejection: 'Final Rejection',
  toss: 'Toss',
  tossDown: 'Toss Down',
  tossUp: 'Toss Up',
  
  // Geometry
  trackCell: 'Comparison Cell',
  itemCategory: 'Item Category',
  altitude: 'Altitude',
  
  // Evaluation
  criteriaSet: 'Criteria Set',
  sessionResult: 'Session Result',
  decisionClass: 'Decision Class',
  
  // Cluster relationship types (v2.2)
  clusterTypeDuplicate: 'Duplicate',
  clusterTypeVariant: 'Variant',
  clusterTypeComposition: 'Composition',
  clusterTypeDecomposition: 'Decomposition',
} as const;

// =============================================================================
// Backend Field Name Mappings
// =============================================================================

/**
 * Maps backend field names to UI-friendly labels.
 * Use this when displaying field names from database responses.
 */
export const FIELD_LABELS: Record<string, string> = {
  // Item fields
  'item_id': 'Item',
  'item_ids': 'Items',
  'candidate_id': 'Item', // legacy
  'candidate_ids': 'Items', // legacy
  
  // Pool fields
  'item_pool_id': 'Item Pool',
  'item_pool_ids': 'Item Pools',
  'focus_list_id': 'Item Pool', // legacy
  'focus_list_ids': 'Item Pools', // legacy
  
  // Category fields
  'item_category': 'Item Category',
  'candidate_type': 'Item Category', // legacy
  'candidateType': 'Item Category', // legacy
  
  // Role fields
  'ball_holder_user_id': 'Ball Holder',
  'ball_holder_scope': 'Ball Holder Scope',
  'stage_owner': 'Ball Holder', // legacy
  
  // Lifecycle (note: priority is contextual, not a lifecycle state)
  'lifecycle_state': 'State',
  'lifecycle_stage': 'Stage',
};

/**
 * Get a UI-friendly label for a backend field name
 */
export function getFieldLabel(fieldName: string): string {
  return FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// =============================================================================
// Intent / Action Mappings
// =============================================================================

/**
 * Maps backend intent values to UI-friendly labels.
 * TODO: These intents need migration in the backend (CHECK constraints).
 */
export const INTENT_LABELS: Record<string, string> = {
  'select_candidate_pool': 'Select Item Pool', // legacy
  'select_item_pool': 'Select Item Pool',
  'lock_focus_list': 'Lock Item Pool', // legacy
  'lock_item_pool': 'Lock Item Pool',
  'LOCK_FOCUS_LIST': 'Lock Item Pool', // legacy
  'SELECT_CANDIDATE_POOL': 'Select Item Pool', // legacy
};

/**
 * Get a UI-friendly label for a backend intent
 */
export function getIntentLabel(intent: string): string {
  return INTENT_LABELS[intent] || intent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
